<script setup lang="ts">
import {
  X as XIcon,
  AlertCircle as AlertCircleIcon,
  CheckCircle as CheckCircleIcon,
  AlertTriangle as AlertTriangleIcon,
  Info as InfoIcon,
} from '@lucide/vue'
import { storeToRefs } from 'pinia'
import { computed } from 'vue'

import { useEventListener } from '@/composables/useEventListener'
import { isSmallScreen } from '@/services/uiService'
import { useToastStore, type ToastMessage, type ToastType } from '@/stores/toastStore'

const toastStore = useToastStore()
const { toasts } = storeToRefs(toastStore)

// Controls whether the toast container is active (visible)
const isActive = computed(() => toasts.value.length > 0)

// Placement: desktop keeps the bottom-right stack; mobile uses a top safe-area-aware stack so
// transient feedback does not fight the bottom tab bar, bottom sheets, or gesture area.
const toastContainerStyle = computed(() => {
  if (isSmallScreen.value) {
    return {
      top: 'calc(1rem + env(safe-area-inset-top, 0px))',
      width:
        'clamp(14rem, calc(100vw - 8.5rem - env(safe-area-inset-left, 0px) - env(safe-area-inset-right, 0px)), 20rem)',
    }
  }
  return {
    bottom: 'calc(1rem + env(safe-area-inset-bottom, 0px))',
    right: 'calc(1rem + env(safe-area-inset-right, 0px))',
  }
})

const closeToast = (id: string) => {
  toastStore.removeToast(id)
}

/**
 * Show a success toast message
 * @param text The message to display
 * @param duration Duration in ms before auto-close (default 3000, 0 for no auto-close)
 */
function showSuccess(text: string, duration = 3000) {
  return toastStore.addToast({
    type: 'success',
    text,
    duration,
  })
}

/**
 * Show an error toast message
 * @param text The error message to display
 * @param duration Duration in ms before auto-close (default 15000, 0 for no auto-close)
 */
function showError(text: string, duration = 15000) {
  return toastStore.addToast({
    type: 'error',
    text,
    duration,
  })
}

/**
 * Show a warning toast message
 * @param text The warning message to display
 * @param duration Duration in ms before auto-close (default 3000, 0 for no auto-close)
 */
function showWarning(text: string, duration = 8000) {
  return toastStore.addToast({
    type: 'warning',
    text,
    duration,
  })
}

/**
 * Show an info toast message
 * @param text The info message to display
 * @param duration Duration in ms before auto-close (default 3000, 0 for no auto-close)
 */
function showInfo(text: string, duration = 6000) {
  return toastStore.addToast({
    type: 'info',
    text,
    duration,
  })
}

/**
 * Clear all toast messages
 */
function clearToasts() {
  toastStore.clearToasts()
}

const getIconComponent = (type: ToastType) => {
  switch (type) {
    case 'success':
      return CheckCircleIcon
    case 'warning':
      return AlertTriangleIcon
    case 'error':
      return AlertCircleIcon
    case 'info':
      return InfoIcon
    default:
      return CheckCircleIcon
  }
}

const containerClass = computed(() =>
  isSmallScreen.value
    ? 'fixed left-1/2 -translate-x-1/2 pointer-events-none z-10000'
    : 'fixed bottom-4 right-4 w-auto sm:w-80 pointer-events-none z-10000',
)

const getToastItemClass = (toast: ToastMessage) => {
  if (isSmallScreen.value) {
    return [
      'mb-2',
      'flex',
      'items-start',
      'gap-2.5',
      'rounded-2xl',
      'border',
      'border-black/[0.08]',
      'bg-white',
      'px-3',
      'py-2.5',
      'text-gray-800',
      'shadow-[0_10px_28px_rgba(15,23,42,0.16)]',
      'backdrop-blur-xl',
      'transition-all',
      'duration-300',
      'ease-in-out',
      'pointer-events-auto',
      toast.type === 'success'
        ? 'ring-1 ring-emerald-100'
        : toast.type === 'warning'
          ? 'ring-1 ring-amber-100'
          : toast.type === 'info'
            ? 'ring-1 ring-sky-100'
            : 'ring-1 ring-red-100',
    ]
  }

  return [
    'mb-2',
    'flex',
    'items-center',
    'p-3',
    'rounded-md',
    'shadow-md',
    'transition-all',
    'duration-300',
    'ease-in-out',
    'pointer-events-auto',
    toast.type === 'success'
      ? 'bg-green-100 text-green-800 border border-green-200'
      : toast.type === 'warning'
        ? 'bg-yellow-100 text-yellow-800 border border-yellow-200'
        : toast.type === 'info'
          ? 'bg-blue-100 text-blue-800 border border-blue-200'
          : 'bg-red-100 text-red-800 border border-red-200',
  ]
}

const getIconClass = (type: ToastType) => [
  'h-5',
  'w-5',
  'flex-shrink-0',
  isSmallScreen.value ? 'mt-0.5' : 'mr-2',
  type === 'success'
    ? 'text-emerald-600'
    : type === 'warning'
      ? 'text-amber-600'
      : type === 'error'
        ? 'text-red-600'
        : 'text-sky-600',
]

const getCloseButtonClass = (type: ToastType) => [
  'rounded-full',
  'p-1',
  'transition-all',
  'duration-150',
  'ease-out',
  'hover:cursor-pointer',
  'hover:scale-110',
  'hover:rotate-90',
  'focus:outline-none',
  'focus:ring-2',
  'focus:ring-offset-2',
  'active:scale-95',
  isSmallScreen.value
    ? 'text-gray-400 hover:bg-gray-100 hover:text-gray-700'
    : 'hover:bg-white hover:bg-opacity-25',
  type === 'success'
    ? 'focus:ring-emerald-500'
    : type === 'warning'
      ? 'focus:ring-amber-500'
      : type === 'info'
        ? 'focus:ring-sky-500'
        : 'focus:ring-red-500',
]

const outerEnterFromClass = computed(() =>
  isSmallScreen.value ? 'opacity-0 -translate-y-5' : 'opacity-0 translate-y-5',
)
const outerLeaveToClass = computed(() =>
  isSmallScreen.value ? 'opacity-0 -translate-y-5' : 'opacity-0 translate-y-5',
)

const itemEnterFromClass = computed(() =>
  isSmallScreen.value ? 'opacity-0 -translate-y-3' : 'opacity-0 translate-x-8',
)
const itemLeaveToClass = computed(() =>
  isSmallScreen.value ? 'opacity-0 -translate-y-3' : 'opacity-0 translate-x-8',
)

// Track escape key to dismiss all toasts
const handleKeyDown = (e: KeyboardEvent) => {
  if (e.key === 'Escape' && isActive.value) {
    toastStore.clearToasts()
  }
}

useEventListener(document, 'keydown', handleKeyDown)

defineExpose({
  showSuccess,
  showError,
  showWarning,
  showInfo,
  clearToasts,
  removeToast: closeToast,
})
</script>

<template>
  <Transition
    enter-active-class="transition-all duration-300 ease-out"
    :enter-from-class="outerEnterFromClass"
    enter-to-class="opacity-100 translate-y-0"
    leave-active-class="transition-all duration-300 ease-in"
    leave-from-class="opacity-100 translate-y-0"
    :leave-to-class="outerLeaveToClass"
  >
    <div
      v-if="isActive"
      :class="containerClass"
      :style="toastContainerStyle"
      role="status"
      aria-live="polite"
    >
      <TransitionGroup
        enter-active-class="transition-all duration-300 ease-out"
        :enter-from-class="itemEnterFromClass"
        enter-to-class="opacity-100 translate-x-0"
        leave-active-class="transition-all duration-300 ease-in"
        leave-from-class="opacity-100 translate-x-0"
        :leave-to-class="itemLeaveToClass"
      >
        <div v-for="toast in toasts" :key="toast.id" :class="getToastItemClass(toast)">
          <component :is="getIconComponent(toast.type)" :class="getIconClass(toast.type)" />
          <div class="mr-2 flex-1 break-words text-sm leading-snug">{{ toast.text }}</div>
          <button
            @click.stop="closeToast(toast.id)"
            :class="getCloseButtonClass(toast.type)"
            aria-label="Close notification"
          >
            <XIcon class="w-4 h-4 transition-transform duration-150 ease-out" />
          </button>
        </div>
      </TransitionGroup>
    </div>
  </Transition>
</template>
